import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';



const SatisfactionRatings = ({ treatmentId, data, onUpdate }) => {
  const [ratings, setRatings] = useState({
    overallSatisfaction: data?.overallSatisfaction || 0,
    providerRating: data?.providerRating || 0,
    facilityRating: data?.facilityRating || 0,
    communicationRating: data?.communicationRating || 0,
    painManagement: data?.painManagement || 0,
    waitTime: data?.waitTime || 0,
    costTransparency: data?.costTransparency || 0,
    wouldRecommend: data?.wouldRecommend || null,
    additionalComments: data?.additionalComments || ''
  });

  useEffect(() => {
    onUpdate(ratings);
  }, [ratings]);

  const handleRatingChange = (field, value) => {
    setRatings(prev => ({ ...prev, [field]: value }));
  };

  const StarRating = ({ value, onChange, label }) => {
    const [hover, setHover] = useState(0);

    return (
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">{label}</label>
        <div className="flex items-center gap-2">
          {[1, 2, 3, 4, 5]?.map(star => (
            <button
              key={star}
              type="button"
              onClick={() => onChange(star)}
              onMouseEnter={() => setHover(star)}
              onMouseLeave={() => setHover(0)}
              className="transition-transform hover:scale-110"
            >
              <Icon
                name="Star"
                size={32}
                className={`${
                  star <= (hover || value)
                    ? 'text-warning fill-warning' :'text-muted-foreground'
                } transition-colors`}
              />
            </button>
          ))}
          <span className="ml-2 text-sm font-medium text-foreground">
            {value > 0 ? `${value}/5` : 'Not rated'}
          </span>
        </div>
      </div>
    );
  };

  const SliderRating = ({ value, onChange, label, minLabel, maxLabel }) => {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-foreground">{label}</label>
          <span className="text-sm font-bold text-primary">{value}/10</span>
        </div>
        <input
          type="range"
          min="0"
          max="10"
          value={value}
          onChange={(e) => onChange(parseInt(e?.target?.value))}
          className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
        />
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{minLabel}</span>
          <span>{maxLabel}</span>
        </div>
      </div>
    );
  };

  const NPSButtons = ({ value, onChange }) => {
    return (
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          How likely are you to recommend this treatment to others?
        </label>
        <div className="grid grid-cols-11 gap-2">
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]?.map(score => (
            <button
              key={score}
              type="button"
              onClick={() => onChange(score)}
              className={`h-12 rounded-lg border-2 font-semibold transition-all ${
                value === score
                  ? score <= 6
                    ? 'bg-error text-error-foreground border-error'
                    : score <= 8
                    ? 'bg-warning text-warning-foreground border-warning'
                    : 'bg-success text-success-foreground border-success' :'bg-background border-border text-muted-foreground hover:border-primary'
              }`}
            >
              {score}
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Not at all likely</span>
          <span>Extremely likely</span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
          <Icon name="Heart" size={20} className="text-primary" />
          Overall Satisfaction
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Please rate your overall satisfaction with the treatment experience
        </p>
        <StarRating
          label="Overall Treatment Satisfaction"
          value={ratings?.overallSatisfaction}
          onChange={(value) => handleRatingChange('overallSatisfaction', value)}
        />
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
          <Icon name="Users" size={20} className="text-primary" />
          Provider & Facility Ratings
        </h3>
        <div className="space-y-6">
          <StarRating
            label="Provider Professionalism & Expertise"
            value={ratings?.providerRating}
            onChange={(value) => handleRatingChange('providerRating', value)}
          />
          <StarRating
            label="Facility Cleanliness & Environment"
            value={ratings?.facilityRating}
            onChange={(value) => handleRatingChange('facilityRating', value)}
          />
          <StarRating
            label="Communication & Explanation of Treatment"
            value={ratings?.communicationRating}
            onChange={(value) => handleRatingChange('communicationRating', value)}
          />
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
          <Icon name="Activity" size={20} className="text-primary" />
          Treatment Experience
        </h3>
        <div className="space-y-6">
          <SliderRating
            label="Pain Management During Treatment"
            value={ratings?.painManagement}
            onChange={(value) => handleRatingChange('painManagement', value)}
            minLabel="Very Poor"
            maxLabel="Excellent"
          />
          <SliderRating
            label="Wait Time & Scheduling"
            value={ratings?.waitTime}
            onChange={(value) => handleRatingChange('waitTime', value)}
            minLabel="Very Dissatisfied"
            maxLabel="Very Satisfied"
          />
          <SliderRating
            label="Cost Transparency & Billing"
            value={ratings?.costTransparency}
            onChange={(value) => handleRatingChange('costTransparency', value)}
            minLabel="Not Clear"
            maxLabel="Very Clear"
          />
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
          <Icon name="ThumbsUp" size={20} className="text-primary" />
          Recommendation
        </h3>
        <NPSButtons
          value={ratings?.wouldRecommend}
          onChange={(value) => handleRatingChange('wouldRecommend', value)}
        />
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="MessageSquare" size={20} className="text-primary" />
          Additional Comments
        </h3>
        <textarea
          value={ratings?.additionalComments}
          onChange={(e) => handleRatingChange('additionalComments', e?.target?.value)}
          placeholder="Please share any additional feedback about your treatment experience..."
          rows={6}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>

      <div className="bg-primary/5 rounded-lg border border-primary/20 p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Your feedback matters</p>
            <p>Your responses help us improve our services and ensure the best possible care for all patients.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SatisfactionRatings;